/**
 * TOKEN MANAGER — Centralizza il lifecycle dei token utente.
 *
 * Strategia di storage:
 * - accessToken → memoria module-level (volatile al reload).
 *   Mitigazione XSS: niente persistenza = niente esfiltrazione da storage.
 *   Al reload si chiama /user/refresh + /user/init per riavere la sessione.
 * - refreshToken → sessionStorage (persiste al reload, perso alla chiusura
 *   del tab). Scelta conservativa: nessun "stay logged in" cross-sessione.
 * - webId → sessionStorage, serve al /user/refresh per la risoluzione
 *   multi-company lato backend.
 * - apiHost → sessionStorage. Virtual host della company autenticata,
 *   ritornato dal backend nella LoginResponse. In setup multi-tenant la SPA
 *   vive su un host (es. crm.sviluppo) ma il Liferay multi-instance accetta
 *   il Bearer solo se il request raggiunge la company corretta — quindi
 *   tutte le chiamate authed devono andare su <apiHost>. Vedi httpClient.
 */

const REFRESH_TOKEN_KEY = 'crm_refresh_token';
const WEB_ID_KEY = 'crm_web_id';
const API_HOST_KEY = 'crm_api_host';

export interface Tokens {
  accessToken: string;
  refreshToken: string;
  webId: string;
  apiHost?: string;
}

let accessToken: string | null = null;

export function setTokens(tokens: Tokens): void {
  accessToken = tokens.accessToken;
  sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  sessionStorage.setItem(WEB_ID_KEY, tokens.webId);
  if (tokens.apiHost) sessionStorage.setItem(API_HOST_KEY, tokens.apiHost);
}

/**
 * Adotta una sessione ricevuta da un'altra tab (handshake cross-tab): scrive in
 * sessionStorage refreshToken/webId/apiHost SENZA accessToken. Quest'ultimo viene
 * poi rigenerato dal normale flusso /user/refresh (performRefresh).
 */
export function adoptSharedSession(session: {
  refreshToken: string;
  webId: string;
  apiHost: string | null;
}): void {
  sessionStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
  sessionStorage.setItem(WEB_ID_KEY, session.webId);
  if (session.apiHost) sessionStorage.setItem(API_HOST_KEY, session.apiHost);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getWebId(): string | null {
  return sessionStorage.getItem(WEB_ID_KEY);
}

export function setApiHost(apiHost: string | null | undefined): void {
  if (apiHost) sessionStorage.setItem(API_HOST_KEY, apiHost);
}

export function getApiHost(): string | null {
  return sessionStorage.getItem(API_HOST_KEY);
}

export function clearTokens(): void {
  accessToken = null;
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(WEB_ID_KEY);
  sessionStorage.removeItem(API_HOST_KEY);
}
